import React, { useState, useEffect } from 'react';
import {
  Card,
  Input,
  Button,
  Avatar,
  Typography,
  Space,
  Divider,
  Row,
  Col,
  Steps,
  Result,
  Alert
} from 'antd';
import { UserOutlined, DeleteOutlined, SafetyOutlined, CheckCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { apiService } from './utils/api';
import notification from './utils/notification';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import LanguageSwitcher from './components/LanguageSwitcher';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './App.css';
import './utils/test-config'; // 导入配置测试工具

const { Title, Text } = Typography;
const { Step } = Steps;

function AppContent() {
  const { t } = useLanguage();
  const [currentStep, setCurrentStep] = useState(0);
  const [userId, setUserId] = useState('');
  const [userInfo, setUserInfo] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [networkError, setNetworkError] = useState(false);

  // 检测网络连接状态
  useEffect(() => {
    const checkNetwork = async () => {
      try {
        await apiService.testConnection();
        setNetworkError(false);
      } catch (error) {
        console.log('req error', error)
        if (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK') {
          setNetworkError(true);
        }
      }
    };

    checkNetwork();
    const interval = setInterval(checkNetwork, 30000); // 每30秒检查一次

    return () => clearInterval(interval);
  }, []);

    // 获取用户信息
  const handleGetUserInfo = async () => {
    if (!userId.trim()) {
      notification.error(t('messages.enterUserId'));
      return;
    }

    setLoading(true);
    try {
      const userInfo = await apiService.getUserInfo(userId.trim());
      setUserInfo(userInfo);
      setCurrentStep(1);
    } catch (error) {
      console.error('获取用户信息失败:', error);
      if (error.code === 'ECONNABORTED') {
        notification.error(t('messages.networkTimeout'));
      } else if (error.code === 'ERR_NETWORK') {
        notification.error(t('messages.networkError'));
        setNetworkError(true);
      } else {
        // console.log('用户信息不存在')
        notification.error(error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  // 发送验证码
  const handleSendVerificationCode = async () => {
    setLoading(true);
    try {
      const success = await apiService.sendVerificationCode(userId.trim());

      if (success) {
        setCurrentStep(2);
        notification.success(t('messages.sendCodeSuccess'));
      } else {
        notification.error(t('messages.sendCodeFailed'));
      }
    } catch (error) {
      // console.error('发送验证码失败:', error);
      if (error.code === 'ECONNABORTED') {
        notification.error(t('messages.networkTimeout'));
      } else if (error.code === 'ERR_NETWORK') {
        notification.error(t('messages.networkError'));
        setNetworkError(true);
      } else {
        notification.error(error.message || t('messages.sendCodeFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  // 确认注销账户
  const handleDeleteAccount = async () => {
    if (!verificationCode.trim()) {
      notification.error(t('messages.enterCode'));
      return;
    }

    setLoading(true);
    try {
      const success = await apiService.deleteAccount(userId.trim(), verificationCode.trim());

      if (success) {
        setCurrentStep(3);
        notification.success(t('messages.deleteSuccess'));
      } else {
        notification.error(t('messages.deleteFailed'));
      }
    } catch (error) {
      console.error('账户注销失败:', error);
      if (error.code === 'ECONNABORTED') {
        notification.error(t('messages.networkTimeout'));
      } else if (error.code === 'ERR_NETWORK') {
        notification.error(t('messages.networkError'));
        setNetworkError(true);
      } else {
        notification.error(error.message || t('messages.deleteFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  // 重置流程
  const handleReset = () => {
    setCurrentStep(0);
    setUserId('');
    setUserInfo(null);
    setVerificationCode('');
    setNetworkError(false);
  };

  // 重试网络连接
  const handleRetryNetwork = async () => {
    setNetworkError(false);
    notification.info(t('messages.retryNetwork'));
    try {
      await apiService.testConnection();
      notification.success(t('messages.networkNormal'));
    } catch (error) {
      setNetworkError(true);
      notification.error(t('messages.networkStillFailed'));
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <Card className="step-card">
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <div className="step-header">
                <UserOutlined className="step-icon" />
                <Title level={3}>{t('step1FullTitle')}</Title>
              </div>
              <Text type="secondary">{t('step1Instruction')}</Text>

              <Input
                size="large"
                placeholder={t('userIdPlaceholder')}
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                onPressEnter={handleGetUserInfo}
                prefix={<UserOutlined />}
                disabled={networkError}
              />

              <Button
                type="primary"
                size="large"
                onClick={handleGetUserInfo}
                loading={loading}
                disabled={networkError}
                block
              >
                {t('getUserInfoButton')}
              </Button>
            </Space>
          </Card>
        );

      case 1:
        return (
          <Card className="step-card">
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <div className="step-header">
                <SafetyOutlined className="step-icon" />
                <Title level={3}>{t('step2FullTitle')}</Title>
              </div>

              {userInfo && (
                <Card className="user-info-card">
                  <Row gutter={16} align="middle">
                    <Col>
                      <Avatar
                        size={64}
                        src={userInfo.userImg}
                        icon={<UserOutlined />}
                      />
                    </Col>
                    <Col flex="1">
                      <Title level={4} style={{ margin: 0 }}>{userInfo.nickName}</Title>
                      <Text type="secondary">{t('userInfoLabel')}: {userId}</Text>
                    </Col>
                  </Row>
                </Card>
              )}

              <Text type="secondary">
                {t('step2Instruction')}
              </Text>

              <Button
                type="primary"
                size="large"
                onClick={handleSendVerificationCode}
                loading={loading}
                icon={<SafetyOutlined />}
                disabled={networkError}
                block
              >
                {t('sendCodeButton')}
              </Button>
            </Space>
          </Card>
        );

      case 2:
        return (
          <Card className="step-card">
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <div className="step-header">
                <DeleteOutlined className="step-icon" />
                <Title level={3}>{t('step3FullTitle')}</Title>
              </div>

              <div className="verification-notice">
                <Text strong>{t('messages.sendCodeSuccess')}</Text>
                <br />
                <Text type="secondary">
                  {t('step3Instruction')}
                </Text>
              </div>

              <Input
                size="large"
                placeholder={t('codePlaceholder')}
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                onPressEnter={handleDeleteAccount}
                maxLength={6}
                style={{ textAlign: 'center', fontSize: '18px', letterSpacing: '4px' }}
                disabled={networkError}
              />

              <Button
                type="primary"
                size="large"
                onClick={handleDeleteAccount}
                loading={loading}
                icon={<DeleteOutlined />}
                danger
                disabled={networkError}
                block
              >
                {t('confirmDeleteButton')}
              </Button>
            </Space>
          </Card>
        );

      case 3:
        return (
          <Card className="step-card">
            <Result
              status="success"
              icon={<CheckCircleOutlined />}
              title={t('successTitle')}
              subTitle={t('successSubtitle')}
              extra={[
                <Button type="primary" key="reset" onClick={handleReset}>
                  {t('restartButton')}
                </Button>
              ]}
            />
          </Card>
        );

      default:
        return null;
    }
  };

  return (
    <div className="app-container">
      <div className="app-content">
        <div className="app-header">
          <div className="header-content">
            <div className="header-left">
              <Title level={2} className="app-title">
                <DeleteOutlined /> {t('pageTitle')}
              </Title>
              <Text type="secondary">{t('pageSubtitle')}</Text>
            </div>
            <div className="header-right">
              <LanguageSwitcher />
            </div>
          </div>
        </div>

        {networkError && (
          <Alert
            message={t('networkErrorTitle')}
            description={t('networkErrorDesc')}
            type="error"
            showIcon
            icon={<ExclamationCircleOutlined />}
            action={
              <Button size="small" type="primary" onClick={handleRetryNetwork}>
                {t('retryButton')}
              </Button>
            }
            style={{ margin: '0 30px 20px' }}
          />
        )}

        <Steps
          current={currentStep}
          className="app-steps"
          responsive={true}
        >
          <Step title={t('step1Title')} description={t('step1Desc')} />
          <Step title={t('step2Title')} description={t('step2Desc')} />
          <Step title={t('step3Title')} description={t('step3Desc')} />
          <Step title={t('step4Title')} description={t('step4Desc')} />
        </Steps>

        <Divider />

        {renderStepContent()}
      </div>
    </div>
  );
}

function App() {
  return (
    <LanguageProvider>
      <div className="App">
        <AppContent />
        <ToastContainer />
      </div>
    </LanguageProvider>
  );
}

export default App;
